import type { Form } from 'factorial-form'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import type { BranchOperation } from 'modules/automations/lib/Store/Branch'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'
import { FieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

interface Props {
  disabled: boolean
  form: Form
  operation: BranchOperation
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
}

const MISSING_CUSTOM_FIELD = 'missing_custom_field'

const CustomFieldSelect = ({
  disabled,
  form,
  operation,
  loadingCustomFields,
  customFieldsCollection,
}: Props) => {
  const id = operation.id

  const customField = customFieldsCollection.find({
    id,
  })

  const fieldType = customField ? customField.attributes.field_type : MISSING_CUSTOM_FIELD

  const booleanOptions = () => {
    return [
      {
        label: 'true',
        value: true,
      },
      {
        label: 'false',
        value: false,
      },
    ]
  }

  if (loadingCustomFields) return <Loading />

  switch (fieldType) {
    case FieldTypes.text:
    case FieldTypes.cents:
    case FieldTypes.money:
      return (
        <Text
          label={i18n.t('automations.actions.branch.value')}
          field={form.get('value')}
          readonly={disabled}
        />
      )

    case FieldTypes.date:
      return (
        <DayPicker label={i18n.t('automations.actions.branch.value')} field={form.get('value')} />
      )

    case FieldTypes.checkbox:
      return (
        <Select
          none={i18n.t('common.none')}
          readonly={disabled}
          field={form.get('value')}
          label={i18n.t('automations.actions.branch.value')}
          options={booleanOptions()}
        />
      )

    case FieldTypes.singleChoice:
      return (
        <Select
          none={i18n.t('common.none')}
          readonly={disabled}
          field={form.get('value')}
          label={i18n.t('automations.actions.branch.value')}
          options={
            customField?.choiceOptions.map((option) => {
              return {
                label: option.label,
                value: option.id,
              }
            }) || []
          }
        />
      )

    case FieldTypes.longText:
      return (
        <TextArea
          label={i18n.t('automations.actions.branch.value')}
          field={form.get('value')}
          readonly={disabled}
        />
      )

    case MISSING_CUSTOM_FIELD:
      return (
        <TextArea
          label={i18n.t('automations.actions.branch.value')}
          field={form.get('value')}
          readonly={disabled}
        />
      )

    default:
      return (
        <Text
          label={i18n.t('automations.actions.branch.value')}
          field={form.get('value')}
          readonly={disabled}
        />
      )
  }
}

export default CustomFieldSelect
